import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnInit,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';

import { Subscription } from 'rxjs';

import {
  UpdoxDirectMessage,
  MessageFolder,
} from '../updox-direct-message';

import { UpdoxMessagesService } from '../updox-messages.service';
import { UpdoxService } from '../../../../services/updox.service';

export interface FolderStats {
  folder: MessageFolder;
  total: number;
  unread: number;
}

@Component({
  selector: 'app-updox-message-list',
  templateUrl: './message-list.component.html',
})
export class UpdoxMessageListComponent
  implements OnChanges, OnInit, OnDestroy {

  @Input() folder: MessageFolder = 'inbox';
  @Input() practiceId: number;
  @Input() reloadTrigger: boolean;

  /*
   * The parent component owns the selected message.
   * It passes that message back to this component so
   * the selected row can remain highlighted.
   */
  @Input() selectedMessage: UpdoxDirectMessage = null;

  @Output() messageSelected =
    new EventEmitter<UpdoxDirectMessage>();

  @Output() countsChanged =
    new EventEmitter<FolderStats>();

  messages: UpdoxDirectMessage[] = [];

  isLoading = false;
  isLoadingMore = false;

  currentPage = 1;
  totalPages = 1;
  totalCount = 0;

  searchTerm = '';

  private inboundSubscription: Subscription;

  constructor(
    private messagesService: UpdoxMessagesService,
    private updoxService: UpdoxService,
  ) {}

  ngOnInit(): void {
    this.inboundSubscription =
      this.updoxService.inboundMessage.subscribe(json => {
        this.appendInboundMessage(json);
      });
  }

  ngOnDestroy(): void {
    if (this.inboundSubscription) {
      this.inboundSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const shouldReload =
      changes.folder ||
      changes.practiceId ||
      changes.reloadTrigger;

    if (shouldReload && this.practiceId) {
      this.loadMessages();
    }
  }

  /*
   * New inbound message pushed over ActionCable.
   * Prepend it to the inbox message list.
   */
  private appendInboundMessage(json: any): void {
    if (!json || this.folder !== 'inbox') {
      return;
    }

    if (
      this.practiceId &&
      json.practice_id &&
      json.practice_id !== this.practiceId
    ) {
      return;
    }

    if (
      this.messages.some(message => message.id === json.id)
    ) {
      return;
    }

    const inboundMessage =
      new UpdoxDirectMessage().load_from_json(json);

    this.messages = [
      inboundMessage,
      ...this.messages,
    ];

    this.totalCount += 1;

    this.emitCounts();
  }

  loadMessages(): void {
    this.isLoading = true;
    this.currentPage = 1;

    /*
     * Clear the selected message when the folder,
     * practice, or reload trigger changes.
     */
    this.resetSelectedMessage();

    this.messagesService
      .getMessages(
        this.practiceId,
        this.folder,
        1,
      )
      .then(({
        messages,
        page,
        totalPages,
        totalCount,
      }) => {
        this.isLoading = false;

        this.messages = messages.map(message =>
          new UpdoxDirectMessage().load_from_json(message)
        );

        this.currentPage = page;
        this.totalPages = totalPages;
        this.totalCount = totalCount;

        this.emitCounts();
      })
      .catch(() => {
        this.isLoading = false;
        this.messages = [];
        this.totalPages = 1;
        this.totalCount = 0;

        this.emitCounts();
      });
  }

  loadMore(): void {
    if (this.isLoadingMore || !this.hasMore) {
      return;
    }

    this.isLoadingMore = true;

    this.messagesService
      .getMessages(
        this.practiceId,
        this.folder,
        this.currentPage + 1,
      )
      .then(({
        messages,
        page,
        totalPages,
        totalCount,
      }) => {
        this.isLoadingMore = false;

        const nextMessages = messages.map(message =>
          new UpdoxDirectMessage().load_from_json(message)
        );

        this.messages = [
          ...this.messages,
          ...nextMessages,
        ];

        this.currentPage = page;
        this.totalPages = totalPages;
        this.totalCount = totalCount;
      })
      .catch(() => {
        this.isLoadingMore = false;
      });
  }

  get hasMore(): boolean {
    return this.currentPage < this.totalPages;
  }

  /*
   * Called when the user clicks a message row.
   * The selected message is sent to the parent component,
   * which displays it inside updox-detail-col.
   */
  openMessage(message: UpdoxDirectMessage): void {
    if (!message) {
      return;
    }

    this.selectMessage(message);
  }

  selectMessage(message: UpdoxDirectMessage): void {
    if (!message) {
      return;
    }

    /*
     * The parent owns selectedMessage.
     */
    this.messageSelected.emit(message);

    /*
     * Drafts are local-only, so there is no
     * Updox message ID to mark as read.
     */
    if (!message.read && message.folder !== 'draft') {
      message.read = true;

      /*
       * Update the visible unread count immediately.
       */
      this.emitCounts();

      this.messagesService
        .markRead(message.id, true)
        .catch(() => {
          /*
           * Restore the unread state if the request fails.
           */
          message.read = false;
          this.emitCounts();
        });
    }
  }

  get filteredMessages(): UpdoxDirectMessage[] {
    const searchValue = this.searchTerm
      ? this.searchTerm.trim().toLowerCase()
      : '';

    if (!searchValue) {
      return this.messages;
    }

    return this.messages.filter(message => {
      const sender = message.list_sender
        ? message.list_sender.toLowerCase()
        : '';

      const subject = message.display_subject
        ? message.display_subject.toLowerCase()
        : '';

      const preview = message.preview
        ? message.preview.toLowerCase()
        : '';

      return (
        sender.includes(searchValue) ||
        subject.includes(searchValue) ||
        preview.includes(searchValue)
      );
    });
  }

  removeMessage(message: UpdoxDirectMessage): void {
    if (!message) {
      return;
    }

    this.messages = this.messages.filter(
      existingMessage => existingMessage.id !== message.id
    );

    this.totalCount = Math.max(
      0,
      this.totalCount - 1,
    );

    if (
      this.selectedMessage &&
      this.selectedMessage.id === message.id
    ) {
      this.resetSelectedMessage();
    }

    this.emitCounts();
  }

  /*
   * Ask the parent component to clear the selected message.
   */
  private resetSelectedMessage(): void {
    if (this.selectedMessage) {
      this.messageSelected.emit(null);
    }
  }

  private emitCounts(): void {
    const unread = this.messages.filter(
      message => !message.read
    ).length;

    this.countsChanged.emit({
      folder: this.folder,
      total: this.totalCount,
      unread,
    });
  }
}
